import * as React from 'react';
import { connect } from 'react-redux';
import assign from 'object-assign';
import { Modal, Button } from 'react-bootstrap';
import { Translate } from '../../../shared/LanguageService';
import ProductForm from '../productForm/view/ProductForm';
import { editProduct } from '../actions/ProductActions';
import { handleError } from '../../../shared/ErrorCatching';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import {
    IProduct,
    INamedIdentity,
    IProductProperty,
    ISite,
    IEditProduct,
    IProductType,
    IProductBase,
    IProductDeliveryTimes
} from '../Types';

interface IState {
    product: IEditProduct;
    errors: any;
    creating: boolean;
    shouldUpdateAllPhotographers: boolean;
    showConfirmModal: boolean;
}

interface IProps {
    actions: IActions;
    product: IProduct;
    productTypes: IProductType[];
    productProperties: IProductProperty[];
    productDeliveryTimes: IProductDeliveryTimes[];
    bookingConditions: INamedIdentity[];
    legalEntities: INamedIdentity[];
    invoiceProductTypes: INamedIdentity[];
    sites: ISite[];
    close: () => void;
    site: string;
}

class EditProductModal extends React.Component<IProps, IState> {
    constructor(props: IProps, context: any) {
        super(props, context);

        let product: Partial<IEditProduct> = {
            ...(props.product as IProductBase)
        };

        product.type = props.product.type ? this.props.product.type.id.toString() : null;
        product.legalEntity = props.product.legalEntity
            ? this.props.product.legalEntity.id.toString()
            : null;
        product.bookingCondition = this.props.product.bookingCondition
            ? this.props.product.bookingCondition.id.toString()
            : null;
        product.site = this.props.product.site ? this.props.product.site.id.toString() : null;
        product.invoiceProductType = props.product.invoiceProductType
            ? this.props.product.invoiceProductType.id.toString()
            : null;

        let propertyIds: number[] = [];
        this.props.product.properties.forEach((property: IProductProperty) => {
            propertyIds.push(property.id);
        });
        product.properties = propertyIds;

        this.state = {
            product: product as IEditProduct,
            errors: {},
            creating: false,
            shouldUpdateAllPhotographers: false,
            showConfirmModal: false
        };

        this.onChange = this.onChange.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.edit = this.edit.bind(this);
        this.checkConfirmationModal = this.checkConfirmationModal.bind(this);
        this.yesConfirmation = this.yesConfirmation.bind(this);
        this.noConfirmation = this.noConfirmation.bind(this);
        this.closeConfirmModal = this.closeConfirmModal.bind(this);
    }

    render() {
        if (!this.props.product) {
            return null;
        }

        return (
            <div>
                <Modal show={true} onHide={this.closeModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>{Translate('products/modal/title/edit-product')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ProductForm
                            product={this.state.product}
                            errors={this.state.errors}
                            productTypes={this.props.productTypes}
                            productProperties={this.props.productProperties}
                            productDeliveryTimes={this.props.productDeliveryTimes}
                            bookingConditions={this.props.bookingConditions}
                            legalEntities={this.props.legalEntities}
                            invoiceProductTypes={this.props.invoiceProductTypes}
                            onChange={this.onChange}
                            sites={this.props.sites}
                            site={this.props.site}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.closeModal}>
                            {Translate('products/modal/footer/button-label/close')}
                        </Button>
                        <Button
                            disabled={this.state.creating}
                            bsStyle="primary"
                            onClick={this.checkConfirmationModal}
                        >
                            {Translate('products/modal/footer/button-label/save')}
                        </Button>
                        {this.state.showConfirmModal && (
                            <ConfirmationModal
                                size="small"
                                closeModal={this.closeConfirmModal}
                                noAction={this.noConfirmation}
                                title={Translate('products/modal/confirm-modal/title')}
                                text={Translate('products/modal/confirm-modal/content')}
                                doButtonLabel={Translate('products/modal/confirm-modal/button/yes')}
                                cancelButtonLabel={Translate(
                                    'products/modal/confirm-modal/button/no'
                                )}
                                do={this.yesConfirmation}
                                doButtonStyle={'default'}
                            />
                        )}
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }

    edit() {
        this.setState({ creating: true } as IState);
        this.props.actions
            .editProduct(this.state.product)
            .then(() => {
                this.setState({ creating: false } as IState);
                this.closeModal();
            })
            .catch((error: any) =>
                handleError(error, (errors: any): void => {
                    this.setState({
                        errors: errors,
                        creating: false
                    } as IState);
                })
            );
    }

    onChange(product: any) {
        this.setState({ product: product } as IState);
    }

    closeModal() {
        this.props.close();
    }

    checkConfirmationModal() {
        if (this.state.product.isPhotographerProductPreChoosen) {
            this.setState({ showConfirmModal: true } as IState);
        } else {
            this.edit();
        }
    }

    yesConfirmation() {
        let product = assign({}, this.state.product);
        product.shouldUpdateAllPhotographers = true;
        this.setState({ product: product, showConfirmModal: false } as IState);
        this.edit();
    }

    noConfirmation() {
        let product = assign({}, this.state.product);
        product.shouldUpdateAllPhotographers = false;
        this.setState({
            product: assign({}, product),
            showConfirmModal: false
        } as IState);
        this.edit();
    }

    closeConfirmModal() {
        this.setState({ showConfirmModal: false } as IState);
    }
}

function mapStateToProps() {
    return {};
}

interface IActions {
    editProduct: (product: IEditProduct) => Promise<any>;
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            editProduct: (product: any) => dispatch(editProduct(product))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EditProductModal);
